/**
 * カードから sandboxd を呼ぶときの共通処理（sandboxd-client.md §4〜§5、observability.md §5〜§6）。
 * - 採点: `class.card.grade` の下に `class.run` を作り、judgeCases で判定して recordRunOutcome / recordVerdict
 * - 実行だけ（採点しない）: `class.run` だけ
 * ローカル採点の計測は画面側（routes/drill/grade-telemetry.ts）が行う。
 */
import { judgeCases, type JudgeCase, type JudgeResult } from '../../lib/judge';
import type { CardType } from '../../lib/cards';
import {
  byteLength,
  isAbortError,
  ProtocolError,
  SANDBOX_ERROR_MESSAGES,
  SandboxError,
  isRetryable,
  type RunResponse,
} from '../../lib/sandboxd';
import {
  recordRunOutcome,
  recordVerdict,
  withSpan,
  withSpanUnder,
  type RunOutcome,
  type Span,
  type Verdict,
} from '../../lib/telemetry';
import { sandbox } from '../../services';

export interface RunHooks {
  signal?: AbortSignal;
  onRetry?: (attempt: 1 | 2, waitMs: number) => void;
}

function outcomeOf(e: unknown): RunOutcome | null {
  if (!(e instanceof SandboxError)) return e instanceof ProtocolError ? 'http_error' : null;
  switch (e.kind) {
    case 'busy':
      return 'busy';
    case 'timeout':
      return 'gateway_timeout';
    case 'network':
      return 'network_error';
    case 'local-busy':
      return null;
    default:
      return 'http_error';
  }
}

async function runUnder(
  parent: Span | undefined,
  code: string,
  stdins: string[],
  hooks: RunHooks,
  interpret: (res: RunResponse) => { verdict?: Verdict },
): Promise<RunResponse> {
  return withSpanUnder(
    parent,
    'class.run',
    {
      'class.run.purpose': 'card',
      'class.run.cases': stdins.length,
      'class.run.code_bytes': byteLength(code),
    },
    async (span) => {
      let res: RunResponse;
      try {
        res = await sandbox.run(
          { code, stdins },
          {
            ...(hooks.signal ? { signal: hooks.signal } : {}),
            ...(hooks.onRetry ? { onRetry: hooks.onRetry } : {}),
          },
        );
      } catch (e) {
        const outcome = outcomeOf(e);
        if (outcome) recordRunOutcome(span, outcome);
        throw e;
      }
      try {
        const { verdict } = interpret(res);
        recordRunOutcome(span, 'completed', verdict);
      } catch (e) {
        recordRunOutcome(span, 'http_error');
        throw e;
      }
      return res;
    },
  );
}

/** ケースを 1 リクエストで送り、judgeCases で判定する（card-schema.md §3.7）。 */
export function gradeOnSandbox(
  card: { id: string; type: CardType },
  code: string,
  cases: readonly JudgeCase[],
  hooks: RunHooks = {},
): Promise<JudgeResult> {
  return withSpan(
    'class.card.grade',
    { 'class.card.id': card.id, 'class.card.kind': card.type, 'class.grade.method': 'sandbox' },
    async (span) => {
      let judge: JudgeResult | undefined;
      await runUnder(
        span,
        code,
        cases.map((c) => c.in),
        hooks,
        (res) => {
          judge = judgeCases(cases, res);
          return { verdict: judge.overall };
        },
      );
      if (!judge) throw new ProtocolError('判定がありません');
      recordVerdict(span, {
        cardId: card.id,
        cardKind: card.type,
        method: 'sandbox',
        verdict: judge.overall,
      });
      return judge;
    },
  );
}

/** 採点せずに 1 回だけ実行する（書いて AC の「実行」）。 */
export function runOnce(code: string, stdin: string, hooks: RunHooks = {}): Promise<RunResponse> {
  return runUnder(undefined, code, [stdin], hooks, () => ({}));
}

/** 判定を出せなかったときの表示（sandboxd-client.md §5）。null なら何も出さない（中止・実行中）。 */
export function describeRunError(e: unknown): { message: string; retry: boolean } | null {
  if (isAbortError(e)) return null;
  if (e instanceof SandboxError) {
    if (e.kind === 'local-busy') return null;
    return { message: e.message, retry: isRetryable(e.kind) };
  }
  if (e instanceof ProtocolError) return { message: SANDBOX_ERROR_MESSAGES.protocol, retry: true };
  return { message: SANDBOX_ERROR_MESSAGES.network, retry: true };
}
